import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { INDUSTRIES, COUNTRY_LIST, getCitiesForCountry } from '../data/countryCityData';
import { MiniTour } from '../components/MiniTour';
import {
  Users, Plus, Search, Building2, Phone, Mail, MapPin, DollarSign,
  RefreshCw, Star, TrendingUp, Clock, Briefcase, Ticket, Receipt,
  ArrowRight, Crown, AlertTriangle, ChevronRight, Calendar, FileText, Upload
} from 'lucide-react';
import { BulkUploadDialog } from '../components/BulkUploadDialog';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { formatCurrency as fmtCurrencyGlobal } from '../utils/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  individual: { label: 'Individual', color: 'bg-blue-100 text-blue-700' },
  company: { label: 'Company', color: 'bg-violet-100 text-violet-700' },
  franchise: { label: 'Franchise', color: 'bg-amber-100 text-amber-700' },
  retailer: { label: 'Retailer', color: 'bg-emerald-100 text-emerald-700' },
  restaurant: { label: 'Restaurant', color: 'bg-orange-100 text-orange-700' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-700' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  churned: { label: 'Churned', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  suspended: { label: 'Suspended', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
};

const TIER_CONFIG = {
  bronze: { label: 'Bronze', color: 'text-amber-700 bg-amber-50' },
  silver: { label: 'Silver', color: 'text-gray-600 bg-gray-100' },
  gold: { label: 'Gold', color: 'text-yellow-700 bg-yellow-50' },
  platinum: { label: 'Platinum', color: 'text-violet-700 bg-violet-50' },
};

function formatCurrency(val) {
  if (!val && val !== 0) return '—';
  return fmtCurrencyGlobal(val);
}

/* ── Client Card ──────────────────────────────────────────────────── */
function ClientCard({ client, onClick }) {
  const tc = TYPE_CONFIG[client.client_type] || TYPE_CONFIG.other;
  const sc = STATUS_CONFIG[client.status] || STATUS_CONFIG.active;
  const tier = TIER_CONFIG[client.loyalty_tier] || TIER_CONFIG.bronze;

  return (
    <Card className="bg-white border-gray-border hover:shadow-md transition-all cursor-pointer" onClick={() => onClick(client)} data-testid={`client-card-${client.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-sm text-text-primary truncate">{client.contact_name}</h3>
            {client.company_name && <p className="text-xs text-gray-500 truncate flex items-center gap-1"><Building2 className="h-3 w-3" />{client.company_name}</p>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className={cn("w-2 h-2 rounded-full", sc.dot)} />
            <Badge className={cn("text-[10px]", tc.color)}>{tc.label}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400 mb-2 flex-wrap">
          {client.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 flex-shrink-0" />{client.email}</span>}
          {client.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{client.city}</span>}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={cn("text-[10px] gap-1", tier.color)}>
              <Crown className="h-3 w-3" />{tier.label}
            </Badge>
          </div>
          <span className="text-sm font-semibold text-emerald-600">{formatCurrency(client.lifetime_value)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Client Detail Dialog ────────────────────────────────────────── */
function ClientDetail({ client, onClose, onRefresh }) {
  const [timeline, setTimeline] = useState([]);
  const [loadingTL, setLoadingTL] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!client) return;
    setLoadingTL(true);
    axios.get(`${API}/api/clients/${client.id}/timeline`).then(r => setTimeline(r.data)).catch(() => {}).finally(() => setLoadingTL(false));
  }, [client]);

  if (!client) return null;
  const tc = TYPE_CONFIG[client.client_type] || TYPE_CONFIG.other;
  const sc = STATUS_CONFIG[client.status] || STATUS_CONFIG.active;
  const tier = TIER_CONFIG[client.loyalty_tier] || TIER_CONFIG.bronze;

  return (
    <Dialog open={!!client} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />{client.contact_name}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="timeline">Activity Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 pt-2">
            {/* Header badges */}
            <div className="flex flex-wrap gap-2">
              <Badge className={cn("gap-1", sc.color)}><span className={cn("w-1.5 h-1.5 rounded-full", sc.dot)} />{sc.label}</Badge>
              <Badge className={cn("gap-1", tc.color)}>{tc.label}</Badge>
              <Badge className={cn("gap-1", tier.color)}><Crown className="h-3 w-3" />{tier.label}</Badge>
              {client.churn_risk === 'high' && <Badge className="bg-red-100 text-red-700 gap-1"><AlertTriangle className="h-3 w-3" />High Churn Risk</Badge>}
            </div>

            {/* Contact info */}
            <Card className="border">
              <CardContent className="p-3 grid grid-cols-2 gap-3 text-sm">
                {client.company_name && <div><p className="text-gray-400 text-xs">Company</p><p className="font-medium">{client.company_name}</p></div>}
                {client.email && <div><p className="text-gray-400 text-xs">Email</p><p className="font-medium">{client.email}</p></div>}
                {client.phone && <div><p className="text-gray-400 text-xs">Phone</p><p className="font-medium">{client.phone}</p></div>}
                {client.industry && <div><p className="text-gray-400 text-xs">Industry</p><p className="font-medium">{client.industry}</p></div>}
                {client.city && <div><p className="text-gray-400 text-xs">Location</p><p className="font-medium">{client.city}{client.country ? `, ${client.country}` : ''}</p></div>}
                {client.payment_terms && <div><p className="text-gray-400 text-xs">Payment Terms</p><p className="font-medium capitalize">{client.payment_terms.replace('_', ' ')}</p></div>}
              </CardContent>
            </Card>

            {/* Financials */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-100">
                <DollarSign className="h-4 w-4 text-emerald-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-emerald-700">{formatCurrency(client.lifetime_value)}</p>
                <p className="text-[10px] text-emerald-600">Lifetime Value</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                <Receipt className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-blue-700">{formatCurrency(client.total_paid)}</p>
                <p className="text-[10px] text-blue-600">Total Paid</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-100">
                <Clock className="h-4 w-4 text-amber-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-amber-700">{formatCurrency(client.outstanding_balance)}</p>
                <p className="text-[10px] text-amber-600">Outstanding</p>
              </div>
              <div className="bg-violet-50 rounded-lg p-3 text-center border border-violet-100">
                <Star className="h-4 w-4 text-violet-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-violet-700">{client.nps_score ?? '—'}</p>
                <p className="text-[10px] text-violet-600">NPS Score</p>
              </div>
            </div>

            {/* Activity counts */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Field Jobs', count: client.job_count || 0, icon: Briefcase, color: 'text-blue-600' },
                { label: 'Tickets', count: client.ticket_count || 0, icon: Ticket, color: 'text-amber-600' },
                { label: 'Invoices', count: client.invoice_count || 0, icon: Receipt, color: 'text-emerald-600' },
              ].map(a => (
                <div key={a.label} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                  <a.icon className={cn("h-4 w-4", a.color)} />
                  <div><p className="text-sm font-semibold">{a.count}</p><p className="text-[10px] text-gray-400">{a.label}</p></div>
                </div>
              ))}
            </div>

            {client.notes && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2.5 border">{client.notes}</p>}

            {/* Quick Actions — Interconnectivity */}
            <div className="pt-1">
              <p className="text-xs font-medium text-gray-400 mb-2">Quick Actions</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => { onClose(); navigate('/sales/proposals?tab=creator'); }} data-testid="client-create-proposal">
                  <FileText className="h-3 w-3" />Create Proposal
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => { onClose(); navigate('/operations/field-jobs'); }} data-testid="client-create-job">
                  <Briefcase className="h-3 w-3" />Create Job
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => { onClose(); navigate('/sales/invoices'); }} data-testid="client-create-invoice">
                  <Receipt className="h-3 w-3" />Create Invoice
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="pt-2">
            {loadingTL ? (
              <p className="text-xs text-gray-400 text-center py-6">Loading timeline...</p>
            ) : timeline.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No activity recorded yet. Activities will appear here as you interact with this client.</p>
            ) : (
              <div className="space-y-0">
                {timeline.map((a, i) => (
                  <div key={i} className="flex gap-3 py-2.5 border-b border-gray-50 last:border-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-text-primary">{a.summary}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}
                        {a.event && <span className="ml-2 text-gray-300">{a.event}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/* ── Create Client Dialog ────────────────────────────────────────── */
function CreateClientDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    contact_name: '', company_name: '', email: '', phone: '',
    client_type: 'individual', industry: '', address: '', city: '', country: '',
    payment_terms: 'net_30', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('direct'); // direct | from_lead
  const [leads, setLeads] = useState([]);
  const [leadSearch, setLeadSearch] = useState('');

  useEffect(() => {
    if (open && mode === 'from_lead') {
      axios.get(`${API}/api/leads`).then(r => {
        const list = r.data?.leads || r.data || [];
        setLeads(list);
      }).catch(() => {});
    }
  }, [open, mode]);

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => (l.id || l._id || l.lead_id) === leadId);
    if (!lead) return;
    setForm(f => ({
      ...f,
      contact_name: lead.full_name || lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '',
      company_name: lead.company || lead.business_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      industry: lead.industry || '',
      city: lead.city || '',
      country: lead.country || '',
      client_type: lead.company ? 'company' : 'individual',
      notes: `Converted from lead: ${lead.full_name || lead.name || lead.email || ''}`,
    }));
  };

  const handleSave = async () => {
    if (!form.contact_name.trim()) { toast.error('Contact name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/clients`, form);
      toast.success('Client created');
      onCreated();
      onClose();
      setForm({ contact_name: '', company_name: '', email: '', phone: '', client_type: 'individual', industry: '', address: '', city: '', country: '', payment_terms: 'net_30', notes: '' });
      setMode('direct');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  const handleConvertLead = async (leadId) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/clients/convert/${leadId}`);
      if (data.already_client) toast.info('Already a client');
      else toast.success('Lead converted to client!');
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally { setSaving(false); }
  };

  const filteredLeads = leads.filter(l => {
    if (!leadSearch) return true;
    const s = leadSearch.toLowerCase();
    const name = (l.full_name || l.name || `${l.first_name || ''} ${l.last_name || ''}`.trim()).toLowerCase();
    return name.includes(s) || (l.email || '').toLowerCase().includes(s) || (l.company || '').toLowerCase().includes(s);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-client-dialog">
        <DialogHeader><DialogTitle>Add New Client</DialogTitle></DialogHeader>

        {/* Mode Selector */}
        <div className="flex gap-2 mb-2">
          <Button variant={mode === 'direct' ? 'default' : 'outline'} size="sm" className={mode === 'direct' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => setMode('direct')} data-testid="mode-direct">
            <Plus className="h-3.5 w-3.5 mr-1" /> New Client
          </Button>
          <Button variant={mode === 'from_lead' ? 'default' : 'outline'} size="sm" className={mode === 'from_lead' ? 'bg-blue-600 hover:bg-blue-700' : ''} onClick={() => setMode('from_lead')} data-testid="mode-from-lead">
            <ArrowRight className="h-3.5 w-3.5 mr-1" /> From Lead
          </Button>
        </div>

        {mode === 'from_lead' && (
          <div className="space-y-2 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Label className="text-xs text-blue-700 font-semibold">Select a Lead to Convert or Pre-fill</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-blue-400" />
              <Input placeholder="Search leads by name, email, company..." className="pl-8 h-8 text-sm" value={leadSearch} onChange={e => setLeadSearch(e.target.value)} data-testid="lead-search-input" />
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {filteredLeads.length === 0 ? (
                <p className="text-xs text-blue-400 text-center py-3">No leads found</p>
              ) : filteredLeads.slice(0, 20).map(l => {
                const lId = l.id || l._id || l.lead_id;
                const lName = l.full_name || l.name || `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.email;
                return (
                  <div key={lId} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100 hover:border-blue-300 transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{lName}</p>
                      <p className="text-[10px] text-gray-500">{l.email || ''} {l.company ? `· ${l.company}` : ''}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => handleLeadSelect(lId)} data-testid={`prefill-lead-${lId}`}>
                        Pre-fill
                      </Button>
                      <Button size="sm" className="h-6 text-[10px] px-2 bg-blue-600 hover:bg-blue-700" onClick={() => handleConvertLead(lId)} data-testid={`convert-lead-${lId}`}>
                        Convert
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact Name *</Label><Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} data-testid="client-name-input" /></div>
            <div><Label>Company</Label><Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client Type</Label>
              <Select value={form.client_type} onValueChange={v => setForm(f => ({ ...f, client_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Industry</Label>
              <Select value={form.industry} onValueChange={v => setForm(f => ({ ...f, industry: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent className="max-h-[250px]">
                  {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Country</Label>
              <Select value={form.country} onValueChange={v => setForm(f => ({ ...f, country: v, city: '' }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-[250px]">
                  {COUNTRY_LIST.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>City</Label>
              <Select value={form.city} onValueChange={v => setForm(f => ({ ...f, city: v }))} disabled={!form.country}>
                <SelectTrigger className="mt-1"><SelectValue placeholder={form.country ? "Select city" : "Select country first"} /></SelectTrigger>
                <SelectContent className="max-h-[250px]">
                  {getCitiesForCountry(form.country).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Select value={form.payment_terms} onValueChange={v => setForm(f => ({ ...f, payment_terms: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prepaid">Prepaid</SelectItem>
                <SelectItem value="cod">Cash on Delivery</SelectItem>
                <SelectItem value="net_15">Net 15</SelectItem>
                <SelectItem value="net_30">Net 30</SelectItem>
                <SelectItem value="net_60">Net 60</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">{saving ? 'Saving...' : 'Create Client'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main Page ────────────────────────────────────────────────────── */
export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [detailClient, setDetailClient] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.client_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search) params.search = search;
      const [cRes, sRes] = await Promise.allSettled([
        axios.get(`${API}/api/clients`, { params }),
        axios.get(`${API}/api/clients/stats`),
      ]);
      if (cRes.status === 'fulfilled') setClients(cRes.value.data);
      if (sRes.status === 'fulfilled') setStats(sRes.value.data);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, [search, filterType, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleClientClick = async (client) => {
    try {
      const res = await axios.get(`${API}/api/clients/${client.id}`);
      setDetailClient(res.data);
    } catch { setDetailClient(client); }
  };

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Clients<PageInfoTooltip pageKey="clients" /></h1>
          <p className="text-gray-medium text-sm mt-1">Active business relationships — invoices, jobs, and retention</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-import-clients-btn">
            <Upload className="h-4 w-4 mr-1.5" />Import
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-client-btn">
            <Plus className="h-4 w-4 mr-1.5" />New Client
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="bg-white border-gray-border"><CardContent className="p-3"><p className="text-xs text-gray-400">Total Clients</p><p className="text-xl font-bold">{stats.total || 0}</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3"><p className="text-xs text-gray-400">Active</p><p className="text-xl font-bold text-emerald-600">{stats.active || 0}</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3"><p className="text-xs text-gray-400">Churned</p><p className="text-xl font-bold text-red-600">{stats.churned || 0}</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3"><p className="text-xs text-gray-400">Lifetime Value</p><p className="text-xl font-bold text-emerald-600">{formatCurrency(stats.total_lifetime_value)}</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3"><p className="text-xs text-gray-400">Total Paid</p><p className="text-xl font-bold text-blue-600">{formatCurrency(stats.total_paid)}</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3"><p className="text-xs text-gray-400">Outstanding</p><p className="text-xl font-bold text-amber-600">{formatCurrency(stats.total_outstanding)}</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search clients..." className="pl-8" value={search} onChange={e => setSearch(e.target.value)} data-testid="client-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[130px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Client Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {clients.length === 0 && !loading && (
          <div className="col-span-full text-center py-12">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No clients yet. Convert leads or add clients directly.</p>
          </div>
        )}
        {clients.map(c => <ClientCard key={c.id} client={c} onClick={handleClientClick} />)}
      </div>

      {/* Dialogs */}
      <CreateClientDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={fetchAll} />
      <ClientDetail client={detailClient} onClose={() => setDetailClient(null)} onRefresh={fetchAll} />
      <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={fetchAll}
        endpoint="/api/clients/import/csv" entityName="Clients"
        templateFields={["contact_name","company_name","email","phone","client_type","industry","address","city","country","payment_terms","total_revenue","notes"]} />
    </div>
  );
}
